/**
 * Calculate required sample size for a given population
 * Using standard formula for sample size with finite population correction
 *
 * @param {number} populationSize - Total population size
 * @param {number} confidenceLevel - Confidence level (default 0.95 for 95%)
 * @param {number} marginOfError - Margin of error (default 0.05 for 5%)
 * @returns {number} Required sample size
 */
export function calculateSampleSize(populationSize, confidenceLevel = 0.95, marginOfError = 0.05) {
    if (!populationSize || populationSize <= 0) {
        return 100; // Default fallback
    }

    // Z-score lookup for common confidence levels
    const zScores = {
        0.90: 1.645,
        0.95: 1.96,
        0.99: 2.576
    };

    const z = zScores[confidenceLevel] || 1.96;
    const p = 0.5; // Maximum variability (worst case)
    const e = marginOfError;

    // Sample size formula without finite population correction
    const n0 = (z * z * p * (1 - p)) / (e * e);

    // Apply finite population correction
    const n = n0 / (1 + ((n0 - 1) / populationSize));

    // Round up to nearest integer
    return Math.ceil(n);
}

/**
 * Calculate initial tranche size (approximately 1/3 of target sample)
 *
 * @param {number} targetSample - Target sample size
 * @returns {number} Initial tranche size
 */
export function calculateInitialTranche(targetSample) {
    if (!targetSample || targetSample <= 0) {
        return 50; // Default fallback
    }

    // 1/3 of target, rounded up
    return Math.ceil(targetSample / 3);
}

/**
 * Get sample size recommendations for different confidence levels
 *
 * @param {number} populationSize - Total population size
 * @returns {Object} Object with sample sizes for different confidence levels
 */
export function getSampleSizeRecommendations(populationSize) {
    return {
        conservative: calculateSampleSize(populationSize, 0.99, 0.05), // 99% confidence, 5% margin
        standard: calculateSampleSize(populationSize, 0.95, 0.05),     // 95% confidence, 5% margin
        relaxed: calculateSampleSize(populationSize, 0.95, 0.10),      // 95% confidence, 10% margin
    };
}
